import React, { useEffect, useRef, useState } from "react";
import Button from "components/ui/Button";
import { getApiErrorMessage } from "utils/productAnnouncement";

const fieldClass =
  "w-full rounded-md border border-mediumgray bg-white px-3 py-2 text-sm outline-none focus:border-darkgray";

const AnnouncementMedia = ({
  media,
  isNew,
  isDraft,
  isPublished,
  isMediaBusy,
  onUpdate,
  onMove,
  onAdd,
  onRemove,
  onError,
  onPendingFileChange,
}) => {
  const [file, setFile] = useState(null);
  const [altText, setAltText] = useState("");
  const [caption, setCaption] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    onPendingFileChange(Boolean(file));
  }, [file, onPendingFileChange]);

  const clearPicker = () => {
    setFile(null);
    setAltText("");
    setCaption("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleAdd = async () => {
    if (!file) {
      onError("Choose an image or MP4 video first.");
      return;
    }
    if (file.type.startsWith("image/") && !altText.trim()) {
      onError("Alternative text is required for an image.");
      return;
    }
    if (
      file.type.startsWith("video/") &&
      media.some((asset) => asset.type === "video")
    ) {
      onError("Only one video is allowed per announcement.");
      return;
    }
    try {
      await onAdd(file, altText, caption);
      clearPicker();
    } catch (error) {
      onError(getApiErrorMessage(error, "Could not upload the media."));
    }
  };

  const handleRemove = async (asset) => {
    try {
      await onRemove(asset);
      setPendingDelete(null);
    } catch (error) {
      onError(getApiErrorMessage(error, "Could not delete the media."));
    }
  };

  return (
    <section className="rounded-lg border border-mediumgray bg-white p-4">
      <h3 className="text-base font-semibold text-darkergray">Media</h3>
      <p className="mt-1 text-xs text-darkgray">
        Up to 5 files, including at most one MP4. Media appears at the top of
        the announcement, in this order.
        {isNew && " Files upload when you save the draft."}
      </p>
      <div className="mt-4 space-y-4">
        {media.length > 0 && (
          <div className="space-y-3">
            {media.map((asset, index) => {
              const assetKey = asset._id || asset.clientId;
              return (
                <div
                  key={assetKey}
                  className="rounded-lg border border-mediumgray p-3"
                >
                  <div className="flex gap-3">
                    {asset.type === "video" ? (
                      <video
                        src={asset.url}
                        controls
                        className="h-24 w-32 rounded bg-black object-contain"
                      />
                    ) : (
                      <img
                        src={asset.url}
                        alt={asset.altText || ""}
                        className="h-24 w-32 rounded object-cover"
                      />
                    )}
                    <div className="flex-1 space-y-2">
                      <input
                        aria-label={`Media ${index + 1} alternative text`}
                        className={fieldClass}
                        maxLength={200}
                        value={asset.altText || ""}
                        onChange={(event) =>
                          onUpdate(index, "altText", event.target.value)
                        }
                        placeholder={
                          asset.type === "image"
                            ? "Alternative text (required)"
                            : "Video description"
                        }
                      />
                      <input
                        aria-label={`Media ${index + 1} caption`}
                        className={fieldClass}
                        maxLength={240}
                        value={asset.caption || ""}
                        onChange={(event) =>
                          onUpdate(index, "caption", event.target.value)
                        }
                        placeholder="Optional caption"
                      />
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-3 text-xs">
                    <button
                      type="button"
                      onClick={() => onMove(index, -1)}
                      disabled={index === 0}
                      className="text-blue-700 disabled:opacity-30"
                    >
                      Move up
                    </button>
                    <button
                      type="button"
                      onClick={() => onMove(index, 1)}
                      disabled={index === media.length - 1}
                      className="text-blue-700 disabled:opacity-30"
                    >
                      Move down
                    </button>
                    {asset.file && (
                      <button
                        type="button"
                        onClick={() => handleRemove(asset)}
                        className="text-red-700"
                      >
                        Remove
                      </button>
                    )}
                    {!asset.file &&
                      isDraft &&
                      (pendingDelete === assetKey ? (
                        <>
                          <button
                            type="button"
                            onClick={() => handleRemove(asset)}
                            disabled={isMediaBusy}
                            className="font-medium text-red-700 disabled:opacity-40"
                          >
                            Confirm remove
                          </button>
                          <button
                            type="button"
                            onClick={() => setPendingDelete(null)}
                            className="text-darkgray"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setPendingDelete(assetKey)}
                          disabled={isMediaBusy}
                          className="text-red-700 disabled:opacity-40"
                        >
                          Remove
                        </button>
                      ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {isDraft && media.length < 5 && (
          <div className="space-y-3 rounded-lg bg-lightgray p-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,video/mp4"
              onChange={(event) => setFile(event.target.files?.[0] || null)}
              className="block w-full text-sm"
            />
            <input
              className={fieldClass}
              value={altText}
              maxLength={200}
              onChange={(event) => setAltText(event.target.value)}
              placeholder="Alternative text (required for images)"
            />
            <input
              className={fieldClass}
              value={caption}
              maxLength={240}
              onChange={(event) => setCaption(event.target.value)}
              placeholder="Optional caption"
            />
            {file && (
              <p className="text-xs font-medium text-amber-800">
                {isNew ? "Add" : "Upload"} or clear this file before saving.
              </p>
            )}
            <div className="flex gap-2">
              <Button
                onClick={handleAdd}
                className="black-button"
                loading={isMediaBusy}
              >
                {isNew ? "Add media" : "Upload media"}
              </Button>
              {file && (
                <Button
                  onClick={clearPicker}
                  className="gray-button"
                  disabled={isMediaBusy}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        )}
        {isPublished && (
          <p className="text-xs text-darkgray">
            Save caption, alternative-text, or ordering edits here. Duplicate
            the announcement to replace media files.
          </p>
        )}
      </div>
    </section>
  );
};

export default AnnouncementMedia;
